import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowRight, Check, ShieldCheck, LayoutGrid, Users, CalendarClock, MessagesSquare,
  BarChart3, Wallet, Building2, Handshake, GraduationCap, LifeBuoy, CheckCircle2, Loader2,
} from "lucide-react";

/* ------- shared bilingual language state (public pages, no auth) ------- */
function usePubLang() {
  const [lang, setLangState] = useState(localStorage.getItem("foa_pub_lang") || "en");
  const setLang = (l) => { const v = l === "es" ? "es" : "en"; localStorage.setItem("foa_pub_lang", v); setLangState(v); };
  useEffect(() => {
    const on = () => setLangState(localStorage.getItem("foa_pub_lang") || "en");
    window.addEventListener("storage", on);
    return () => window.removeEventListener("storage", on);
  }, []);
  return [lang === "es" ? "es" : "en", setLang];
}

const NAV = {
  en: { home: "Home", features: "Features", pricing: "Pricing", demo: "Request a Demo", signin: "Sign In" },
  es: { home: "Inicio", features: "Funciones", pricing: "Precios", demo: "Solicitar Demo", signin: "Iniciar Sesión" },
};

function PublicShell({ lang, setLang, children, active, testid }) {
  const n = NAV[lang];
  const links = [["/", n.home], ["/features", n.features], ["/pricing", n.pricing], ["/request-demo", n.demo]];
  return (
    <div className="min-h-screen bg-white text-[#17324D]" data-testid={testid}>
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-6 px-5 py-3">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="pub-logo" aria-label="Fortunate Operations — Home">
              <img src="/fo-logo-trimmed.png" alt="Fortunate Operations" className="h-11 md:h-[3.5rem] w-auto object-contain" />
            </Link>
            <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
              {links.map(([to, label]) => (
                <Link key={to} to={to} data-testid={`pub-nav-${to === "/" ? "home" : to.slice(1)}`}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors hover:text-[#B28A3D] ${active === to ? "text-[#B28A3D]" : "text-[#17324D]/80"}`}>{label}</Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-full border border-slate-200" role="group" aria-label="Language">
              {["en", "es"].map((l) => (
                <button key={l} onClick={() => setLang(l)} data-testid={`pub-lang-${l}`} aria-pressed={lang === l}
                  className={`px-2.5 py-1 text-[11px] font-semibold ${lang === l ? "bg-[#17324D] text-white" : "bg-white text-[#17324D]/70"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
            <Link to="/login"><Button size="sm" variant="outline" data-testid="pub-signin">{NAV[lang].signin}</Button></Link>
            <Link to="/request-demo" className="hidden sm:block"><Button size="sm" data-testid="pub-cta-demo">{NAV[lang].demo}</Button></Link>
          </div>
        </div>
      </header>
      {children}
      <footer className="border-t border-slate-200 bg-[#17324D] text-slate-300">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-5 py-8 text-sm sm:flex-row sm:items-center sm:justify-between">
          <img src="/fo-logo-light-trimmed.png" alt="Fortunate Operations" className="h-9 w-auto object-contain" />
          <div className="flex flex-wrap gap-4 text-xs">
            <Link to="/features" className="hover:text-white">{NAV[lang].features}</Link>
            <Link to="/pricing" className="hover:text-white">{NAV[lang].pricing}</Link>
            <Link to="/request-demo" className="hover:text-white">{NAV[lang].demo}</Link>
            <Link to="/privacy" className="hover:text-white">{lang === "es" ? "Privacidad" : "Privacy"}</Link>
            <Link to="/terms" className="hover:text-white">{lang === "es" ? "Términos" : "Terms"}</Link>
            <Link to="/contact" className="hover:text-white">{lang === "es" ? "Contacto" : "Contact"}</Link>
          </div>
          <div className="text-[11px] text-slate-500">© Fortunate Operations</div>
        </div>
      </footer>
    </div>
  );
}

const Section = ({ children, className = "" }) => (
  <section className={`mx-auto w-full max-w-6xl px-5 ${className}`}>{children}</section>
);

/* ============================== HOME ============================== */
export function PublicHome() {
  const [lang, setLang] = usePubLang();
  const en = lang === "en";
  const t = {
    kicker: en ? "Bilingual operating & growth system for insurance agencies" : "Sistema bilingüe de operación y crecimiento para agencias de seguros",
    h1: en ? "One command center for your entire agency." : "Un centro de mando para toda su agencia.",
    sub: en
      ? "Fortunate Operations brings leads, clients, follow-ups, applications, policies, appointments, communications, accountability, billing, and reporting into a single bilingual command center — with managed implementation and ongoing operational support."
      : "Fortunate Operations reúne prospectos, clientes, seguimientos, solicitudes, pólizas, citas, comunicaciones, responsabilidad, facturación y reportes en un único centro de mando bilingüe — con implementación gestionada y soporte operativo continuo.",
    primary: en ? "Request a Demo" : "Solicitar una Demo",
    secondary: en ? "See Features" : "Ver Funciones",
    layerT: en ? "Works your way" : "Funciona a su manera",
    layer1: en ? "As your agency's primary operating layer" : "Como la capa operativa principal de su agencia",
    layer2: en ? "Or alongside your existing AMS, carrier portal, enrollment platform, or quoting system" : "O junto con su AMS, portal de aseguradora, plataforma de inscripción o sistema de cotización existente",
    serviceT: en ? "Software plus a team" : "Software más un equipo",
    serviceB: en ? "Fortunate Operations includes managed implementation and ongoing operational support — not merely software access." : "Fortunate Operations incluye implementación gestionada y soporte operativo continuo — no solo acceso al software.",
    implT: en ? "Managed implementation may include" : "La implementación gestionada puede incluir",
    ongoingT: en ? "Ongoing service may include" : "El servicio continuo puede incluir",
  };
  const impl = en
    ? ["Agency & user setup", "Workflow mapping", "Pipeline configuration", "Campaign & consent configuration", "Bilingual configuration", "Data-import assistance", "Dashboard setup", "Provider-setup assistance", "Staff training", "Launch support"]
    : ["Configuración de agencia y usuarios", "Mapeo de flujos de trabajo", "Configuración del embudo", "Configuración de campañas y consentimiento", "Configuración bilingüe", "Asistencia de importación de datos", "Configuración de tableros", "Asistencia de configuración de proveedores", "Capacitación del personal", "Soporte de lanzamiento"];
  const ongoing = en
    ? ["Platform access", "Managed administration", "Workflow monitoring", "Provider-status monitoring", "Reporting review", "Minor configuration support", "Team support"]
    : ["Acceso a la plataforma", "Administración gestionada", "Monitoreo de flujos de trabajo", "Monitoreo de estado de proveedores", "Revisión de reportes", "Soporte de configuración menor", "Soporte al equipo"];
  const heroChips = en
    ? ["Leads & Pipeline", "Clients & Policies", "Follow-ups", "Applications", "Appointments", "Reporting & Billing"]
    : ["Prospectos y Embudo", "Clientes y Pólizas", "Seguimientos", "Solicitudes", "Citas", "Reportes y Facturación"];
  return (
    <PublicShell lang={lang} setLang={setLang} active="/" testid="public-home">
      <div className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50">
        <Section className="grid items-center gap-10 py-16 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:py-24">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#17324D]/5 px-3 py-1 text-xs font-medium text-[#17324D]/70">
              <ShieldCheck className="h-3.5 w-3.5 text-[#B28A3D]" /> {t.kicker}
            </div>
            <h1 className="font-heading text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl" data-testid="home-h1">{t.h1}</h1>
            <p className="mt-5 text-base leading-relaxed text-[#17324D]/70 sm:text-lg">{t.sub}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/request-demo"><Button size="lg" data-testid="home-cta-demo">{t.primary} <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
              <Link to="/features"><Button size="lg" variant="outline" data-testid="home-cta-features">{t.secondary}</Button></Link>
            </div>
            <div className="mt-6 flex items-center gap-2 text-xs text-[#17324D]/55">
              <ShieldCheck className="h-4 w-4 text-[#B28A3D]" />
              {en ? "Bilingual · secure role-based access · encrypted credential vault" : "Bilingüe · acceso seguro por roles · bóveda de credenciales cifrada"}
            </div>
          </div>
          <div className="relative hidden lg:block" aria-hidden="true">
            <div className="relative overflow-hidden rounded-3xl bg-[#17324D] p-8 shadow-xl ring-1 ring-[#B28A3D]/30">
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#B28A3D]/20 blur-3xl" />
              <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-[#315A7D]/40 blur-3xl" />
              <img src="/fo-logo-light-trimmed.png" alt="" className="relative w-56 object-contain" />
              <div className="relative mt-7 grid grid-cols-2 gap-2.5">
                {heroChips.map((c) => (
                  <div key={c} className="flex items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-xs text-slate-200 ring-1 ring-white/10">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#B28A3D]" /> {c}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>
      <Section className="py-14">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#B28A3D]">{t.layerT}</div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          {[t.layer1, t.layer2].map((x, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-5">
              <LayoutGrid className="mt-0.5 h-5 w-5 shrink-0 text-[#B28A3D]" />
              <div className="text-sm font-medium">{x}</div>
            </div>
          ))}
        </div>
      </Section>
      <div className="bg-slate-50">
        <Section className="py-14">
          <h2 className="font-heading text-2xl font-bold">{t.serviceT}</h2>
          <p className="mt-2 max-w-3xl text-sm text-[#17324D]/70">{t.serviceB}</p>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 font-semibold"><Handshake className="h-4 w-4 text-[#B28A3D]" /> {t.implT}</div>
              <ul className="mt-3 grid gap-1.5 text-sm text-[#17324D]/80">
                {impl.map((x) => <li key={x} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#B28A3D]" /> {x}</li>)}
              </ul>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 font-semibold"><LifeBuoy className="h-4 w-4 text-[#B28A3D]" /> {t.ongoingT}</div>
              <ul className="mt-3 grid gap-1.5 text-sm text-[#17324D]/80">
                {ongoing.map((x) => <li key={x} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#B28A3D]" /> {x}</li>)}
              </ul>
            </div>
          </div>
        </Section>
      </div>
      <Section className="py-16 text-center">
        <h2 className="font-heading text-3xl font-bold">{en ? "Ready to see it in action?" : "¿Listo para verlo en acción?"}</h2>
        <div className="mt-6"><Link to="/request-demo"><Button size="lg" data-testid="home-cta-demo-bottom">{t.primary} <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link></div>
      </Section>
    </PublicShell>
  );
}

/* ============================== FEATURES ============================== */
export function PublicFeatures() {
  const [lang, setLang] = usePubLang();
  const en = lang === "en";
  const feats = en ? [
    ["Leads & Pipeline", "Capture, assign, and move leads through clear stages with a prioritized work queue.", LayoutGrid],
    ["Clients & Policies", "A single record of clients, applications, and policies across their lifecycle.", Users],
    ["Appointments", "Schedule, confirm, reschedule, and remind — with availability and public booking.", CalendarClock],
    ["Communications", "Consent-aware SMS and calling workflow with an auditable communication history.", MessagesSquare],
    ["Accountability & Reporting", "Command-center dashboards, work queues, and reporting from your real data.", BarChart3],
    ["Billing & Reporting", "Flexible commercial models with a usage ledger and platform reporting.", Wallet],
  ] : [
    ["Prospectos y Embudo", "Capture, asigne y mueva prospectos por etapas claras con una cola de trabajo priorizada.", LayoutGrid],
    ["Clientes y Pólizas", "Un registro único de clientes, solicitudes y pólizas a lo largo de su ciclo de vida.", Users],
    ["Citas", "Programe, confirme, reprograme y recuerde — con disponibilidad y reserva pública.", CalendarClock],
    ["Comunicaciones", "Flujo de SMS y llamadas basado en consentimiento con historial auditable.", MessagesSquare],
    ["Responsabilidad y Reportes", "Tableros de centro de mando, colas de trabajo y reportes con sus datos reales.", BarChart3],
    ["Facturación y Reportes", "Modelos comerciales flexibles con registro de uso y reportes de plataforma.", Wallet],
  ];
  const notT = en ? "What Fortunate Operations does not currently provide" : "Lo que Fortunate Operations no proporciona actualmente";
  const notList = en
    ? ["Carrier downloads", "ACORD form automation", "Carrier quoting", "Carrier enrollment", "Commission reconciliation", "A native mobile application", "Integrations that are not actually configured"]
    : ["Descargas de aseguradoras", "Automatización de formularios ACORD", "Cotización de aseguradoras", "Inscripción con aseguradoras", "Conciliación de comisiones", "Una aplicación móvil nativa", "Integraciones que no están realmente configuradas"];
  return (
    <PublicShell lang={lang} setLang={setLang} active="/features" testid="public-features">
      <Section className="py-14">
        <h1 className="font-heading text-4xl font-extrabold sm:text-5xl" data-testid="features-h1">{en ? "Everything your agency runs on — together." : "Todo con lo que opera su agencia — junto."}</h1>
        <p className="mt-4 max-w-2xl text-[#17324D]/70">{en ? "A connected operating layer for bilingual insurance teams." : "Una capa operativa conectada para equipos de seguros bilingües."}</p>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {feats.map(([title, body, Icon]) => (
            <div key={title} className="rounded-xl border border-slate-200 bg-white p-6 transition-shadow hover:shadow-md">
              <Icon className="h-6 w-6 text-[#B28A3D]" />
              <div className="mt-3 font-semibold">{title}</div>
              <p className="mt-1.5 text-sm text-[#17324D]/70">{body}</p>
            </div>
          ))}
        </div>
      </Section>
      <div className="bg-slate-50">
        <Section className="py-12">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-6" data-testid="features-honesty">
            <div className="font-semibold text-amber-900">{notT}</div>
            <ul className="mt-3 grid gap-1.5 text-sm text-amber-900/80 sm:grid-cols-2">
              {notList.map((x) => <li key={x} className="flex items-start gap-2">• {x}</li>)}
            </ul>
          </div>
        </Section>
      </div>
      <Section className="py-14 text-center">
        <Link to="/request-demo"><Button size="lg" data-testid="features-cta">{en ? "Request a Demo" : "Solicitar una Demo"} <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
      </Section>
    </PublicShell>
  );
}

/* ============================== PRICING ============================== */
export function PublicPricing() {
  const [lang, setLang] = usePubLang();
  const en = lang === "en";
  const models = en
    ? [["Fixed monthly subscription", "A predictable monthly platform fee."],
       ["Per application", "Pay per application milestone."],
       ["Per policy", "Pay per policy milestone."],
       ["Hybrid / performance-based", "A blend tailored to your goals."]]
    : [["Suscripción mensual fija", "Una tarifa mensual predecible de la plataforma."],
       ["Por solicitud", "Pague por hito de solicitud."],
       ["Por póliza", "Pague por hito de póliza."],
       ["Híbrido / por desempeño", "Una combinación adaptada a sus objetivos."]];
  const tiers = [
    { name: en ? "Founding Agency" : "Agencia Fundadora", testid: "tier-founding",
      lines: en ? ["From $5,000 implementation", "From $1,500 per month"] : ["Desde $5,000 de implementación", "Desde $1,500 por mes"],
      note: en ? "Final pricing depends on agency size, workflow, integrations, data migration, and support requirements."
               : "El precio final depende del tamaño de la agencia, el flujo de trabajo, las integraciones, la migración de datos y los requisitos de soporte.",
      cta: en ? "Request a Demo" : "Solicitar una Demo", highlight: true },
    { name: en ? "Growth Agency" : "Agencia en Crecimiento", testid: "tier-growth",
      lines: [en ? "Custom proposal" : "Propuesta personalizada"],
      note: en ? "Tailored to a growing team's volume and support needs." : "Adaptada al volumen y las necesidades de soporte de un equipo en crecimiento.",
      cta: en ? "Request a Demo" : "Solicitar una Demo", highlight: false },
    { name: en ? "Custom / Enterprise" : "Personalizado / Empresa", testid: "tier-custom",
      lines: [en ? "Contact sales" : "Contactar a ventas"],
      note: en ? "For multi-location or high-volume operations." : "Para operaciones de múltiples ubicaciones o alto volumen.",
      cta: en ? "Contact Sales" : "Contactar a Ventas", highlight: false },
  ];
  return (
    <PublicShell lang={lang} setLang={setLang} active="/pricing" testid="public-pricing">
      <Section className="py-14">
        <h1 className="font-heading text-4xl font-extrabold sm:text-5xl" data-testid="pricing-h1">{en ? "Pricing that fits how you grow." : "Precios que se ajustan a cómo crece."}</h1>
        <p className="mt-4 max-w-2xl text-[#17324D]/70">{en
          ? "Choose a commercial model that matches your agency. A separate implementation/setup fee may accompany any model."
          : "Elija un modelo comercial que se ajuste a su agencia. Una tarifa separada de implementación/configuración puede acompañar cualquier modelo."}</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {models.map(([m, d]) => (
            <div key={m} className="rounded-xl border border-slate-200 bg-white p-5" data-testid="pricing-model">
              <div className="font-semibold">{m}</div>
              <p className="mt-1 text-sm text-[#17324D]/70">{d}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-[#17324D]/60">{en
          ? "Per-application, per-policy, and hybrid structures are available by custom proposal."
          : "Las estructuras por solicitud, por póliza e híbridas están disponibles mediante propuesta personalizada."}</p>

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {tiers.map((tier) => (
            <div key={tier.name} data-testid={tier.testid}
              className={`flex flex-col rounded-2xl border p-6 ${tier.highlight ? "border-[#B28A3D] bg-[#17324D] text-white shadow-lg" : "border-slate-200 bg-white"}`}>
              <div className="text-sm font-semibold uppercase tracking-wide" style={{ color: tier.highlight ? "#d9b978" : "#B28A3D" }}>{tier.name}</div>
              <div className="mt-3 space-y-1">
                {tier.lines.map((l) => <div key={l} className="text-lg font-bold">{l}</div>)}
              </div>
              <p className={`mt-3 text-xs ${tier.highlight ? "text-slate-300" : "text-[#17324D]/60"}`}>{tier.note}</p>
              <div className="mt-6">
                <Link to="/request-demo"><Button className="w-full" variant={tier.highlight ? "secondary" : "default"} data-testid={`${tier.testid}-cta`}>{tier.cta}</Button></Link>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-[#17324D]/50">{en
          ? "Fortunate Operations includes managed implementation and ongoing operational support, not merely software access."
          : "Fortunate Operations incluye implementación gestionada y soporte operativo continuo, no solo acceso al software."}</p>
      </Section>
    </PublicShell>
  );
}

/* ============================== REQUEST DEMO ============================== */
export function PublicRequestDemo() {
  const [lang, setLang] = usePubLang();
  const navigate = useNavigate();
  const en = lang === "en";
  const [f, setF] = useState({ preferred_language: lang });
  const [consent, setConsent] = useState({ privacy_ack: false, email_consent: false, sms_consent: false });
  const [texts, setTexts] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  useEffect(() => { api.get("/public/consent-text").then(({ data }) => setTexts(data)).catch(() => {}); }, []);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const T = en ? {
    h1: "Request a demo", sub: "Tell us about your agency and we'll set up a tailored walkthrough.",
    fn: "First name", ln: "Last name", ag: "Agency name", em: "Business email", ph: "Phone", st: "State",
    at: "Agency type", nu: "Number of users / agents", pl: "Preferred language", cs: "Current systems or tools",
    pc: "Primary operational challenge", mlv: "Approx. monthly lead volume", pcm: "Preferred contact method",
    pdt: "Preferred demo timing", notes: "Notes", submit: "Request Demo", required: "Please complete the required fields.",
    thanks: "Thank you — your demo request was received.", ref: "Reference", another: "Submit another request",
    english: "English", spanish: "Spanish",
  } : {
    h1: "Solicitar una demo", sub: "Cuéntenos sobre su agencia y prepararemos una demostración personalizada.",
    fn: "Nombre", ln: "Apellido", ag: "Nombre de la agencia", em: "Correo comercial", ph: "Teléfono", st: "Estado",
    at: "Tipo de agencia", nu: "Número de usuarios / agentes", pl: "Idioma preferido", cs: "Sistemas o herramientas actuales",
    pc: "Principal desafío operativo", mlv: "Volumen mensual aprox. de prospectos", pcm: "Método de contacto preferido",
    pdt: "Horario preferido para la demo", notes: "Notas", submit: "Solicitar Demo", required: "Complete los campos obligatorios.",
    thanks: "Gracias — recibimos su solicitud de demostración.", ref: "Referencia", another: "Enviar otra solicitud",
    english: "Inglés", spanish: "Español",
  };
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.first_name || !f.last_name || !f.agency_name || !f.business_email || !f.phone || !consent.privacy_ack) {
      setError(T.required); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/request-demo", {
        ...f, preferred_language: f.preferred_language || lang,
        privacy_ack: consent.privacy_ack, email_consent: consent.email_consent, sms_consent: consent.sms_consent,
        source_page: "/request-demo", company_website: f.company_website || "",
      });
      setDone(data.display_id || "OK");
    } catch (e2) {
      setError(formatApiErrorDetail(e2?.response?.data?.detail) || T.required);
    } finally { setBusy(false); }
  };
  const field = (k, label, type = "text") => (
    <div className="space-y-1"><Label className="text-xs">{label}</Label>
      <Input type={type} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`demo-${k}`} /></div>
  );
  return (
    <PublicShell lang={lang} setLang={setLang} active="/request-demo" testid="public-request-demo">
      <Section className="py-14">
        <div className="mx-auto max-w-2xl">
          <h1 className="font-heading text-3xl font-extrabold sm:text-4xl" data-testid="demo-h1">{T.h1}</h1>
          <p className="mt-2 text-sm text-[#17324D]/70">{T.sub}</p>
          {done ? (
            <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="demo-success">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
              <div className="mt-3 font-semibold text-emerald-900">{T.thanks}</div>
              {done !== "OK" && <div className="mt-1 text-xs text-emerald-800">{T.ref}: {done}</div>}
              <div className="mt-4 flex justify-center gap-2">
                <Button variant="outline" onClick={() => { setDone(null); setF({ preferred_language: lang }); setConsent({ privacy_ack: false, email_consent: false, sms_consent: false }); }} data-testid="demo-another">{T.another}</Button>
                <Link to="/"><Button>{NAV[lang].home}</Button></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4 rounded-xl border border-slate-200 bg-white p-6" data-testid="demo-form">
              {/* honeypot (hidden from users) */}
              <input type="text" tabIndex={-1} autoComplete="off" value={f.company_website || ""} onChange={(e) => set("company_website", e.target.value)}
                className="hidden" aria-hidden="true" data-testid="demo-hp" />
              <div className="grid gap-4 sm:grid-cols-2">
                {field("first_name", T.fn)}{field("last_name", T.ln)}
                {field("agency_name", T.ag)}{field("business_email", T.em, "email")}
                {field("phone", T.ph, "tel")}{field("state", T.st)}
                {field("agency_type", T.at)}{field("num_users", T.nu)}
                <div className="space-y-1"><Label className="text-xs">{T.pl}</Label>
                  <select value={f.preferred_language || lang} onChange={(e) => set("preferred_language", e.target.value)} data-testid="demo-preferred_language"
                    className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm">
                    <option value="en">{T.english}</option><option value="es">{T.spanish}</option>
                  </select></div>
                {field("monthly_lead_volume", T.mlv)}
                {field("preferred_contact_method", T.pcm)}{field("preferred_demo_timing", T.pdt)}
              </div>
              {field("current_systems", T.cs)}
              {field("primary_challenge", T.pc)}
              <div className="space-y-1"><Label className="text-xs">{T.notes}</Label>
                <textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="demo-notes"
                  className="min-h-20 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm" /></div>

              {texts && (
                <div className="space-y-2 rounded-lg bg-slate-50 p-4 text-xs">
                  <label className="flex items-start gap-2" data-testid="demo-consent-privacy">
                    <input type="checkbox" className="mt-0.5" checked={consent.privacy_ack} onChange={(e) => setConsent((c) => ({ ...c, privacy_ack: e.target.checked }))} />
                    <span><span className="text-red-600">*</span> {texts.privacy_ack[lang]}</span>
                  </label>
                  <label className="flex items-start gap-2" data-testid="demo-consent-email">
                    <input type="checkbox" className="mt-0.5" checked={consent.email_consent} onChange={(e) => setConsent((c) => ({ ...c, email_consent: e.target.checked }))} />
                    <span>{texts.email_consent[lang]}</span>
                  </label>
                  <label className="flex items-start gap-2" data-testid="demo-consent-sms">
                    <input type="checkbox" className="mt-0.5" checked={consent.sms_consent} onChange={(e) => setConsent((c) => ({ ...c, sms_consent: e.target.checked }))} />
                    <span>{texts.sms_consent[lang]}</span>
                  </label>
                </div>
              )}
              {error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="demo-error">{error}</div>}
              <Button type="submit" className="w-full" disabled={busy} data-testid="demo-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{T.submit} <ArrowRight className="ml-1.5 h-4 w-4" /></>}
              </Button>
            </form>
          )}
        </div>
      </Section>
    </PublicShell>
  );
}
